// „Neues Ticket“ für den Ticket-Bearbeiter (Hamdi): rotes Zähler-Badge im Menü + im Tab-Titel,
// rotes NEU-Etikett an der Ticket-Zeile bis zum Anklicken und ein Popup, wenn etwas Neues eintrifft.
// Daten von /api/ticket-neu. Empfänger („aktiv“) fragen alle 10 s nach; alle anderen seltener (45 s),
// weil sie nur Hinweise zu ihren EIGENEN Tickets bekommen (Status geändert, Rückfrage, Antwort):
// kleine Meldung unten rechts + Zähler im Menü „Meine Tickets“.
// Läuft auf JEDEM Modul-Host (Terminliste, Einkauf, Tickets …).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type TicketNeuDaten, type TicketMelderHinweis } from '../lib/api';
import { statusInfo } from '../lib/ticketStatus';
import { fanfare } from './EinkaufNeu';

const LEER: TicketNeuDaten = { aktiv: false, vorname: null, tickets: [], melder: [] };
const POLL_MS = 10_000;           // Empfänger (Hamdi)
const POLL_MELDER_MS = 45_000;    // alle anderen: nur Hinweise zu eigenen Tickets
const MELDER_KEY = 'ticket-melder-gezeigt';
const POPUP_KEY = 'ticket-neu-popup-gezeigt';
const TON_KEY = 'ticket-neu-ton-aus';
const tonAn = (): boolean => { try { return localStorage.getItem(TON_KEY) !== '1'; } catch { return true; } };

type Ctx = {
  daten: TicketNeuDaten;
  /** Ticket-IDs als gesehen markieren → NEU verschwindet sofort */
  gesehen: (ids: number[]) => void;
  istNeu: (id: number) => boolean;
  /** Melder hat sein Ticket angesehen → Hinweis (Toast/Zähler) verschwindet */
  melderGesehen: (ids: number[]) => void;
  hatHinweis: (id: number) => boolean;
};
const TicketNeuContext = createContext<Ctx>({ daten: LEER, gesehen: () => {}, istNeu: () => false, melderGesehen: () => {}, hatHinweis: () => false });
export const useTicketNeu = () => useContext(TicketNeuContext);

function gezeigtLesen(): Set<number> {
  try { return new Set<number>(JSON.parse(localStorage.getItem(POPUP_KEY) ?? '[]')); } catch { return new Set(); }
}
function gezeigtSchreiben(s: Set<number>) {
  try { localStorage.setItem(POPUP_KEY, JSON.stringify([...s].slice(-400))); } catch { /* privat/blockiert */ }
}
const melderKey = (h: TicketMelderHinweis) => `${h.id}:${h.am}`;
function melderGezeigtLesen(): Set<string> {
  try { return new Set<string>(JSON.parse(localStorage.getItem(MELDER_KEY) ?? '[]')); } catch { return new Set(); }
}
function melderGezeigtSchreiben(s: Set<string>) {
  try { localStorage.setItem(MELDER_KEY, JSON.stringify([...s].slice(-300))); } catch { /* egal */ }
}

export function TicketNeuProvider({ children }: { children: ReactNode }) {
  const [daten, setDaten] = useState<TicketNeuDaten>(LEER);
  const geschlossen = useRef(false);
  const aktivRef = useRef<boolean | null>(null);   // null = noch nicht geantwortet

  const laden = useCallback(async () => {
    try {
      const d = await api.ticketNeu.list();
      if (!d) return;
      aktivRef.current = !!d.aktiv;
      if (!geschlossen.current && Array.isArray(d.tickets)) setDaten({ ...d, melder: Array.isArray(d.melder) ? d.melder : [] });
    } catch { /* offline / nicht angemeldet: Badge bleibt wie es ist */ }
  }, []);

  useEffect(() => {
    geschlossen.current = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schleife = () => {
      timer = setTimeout(async () => { await laden(); if (!geschlossen.current) schleife(); },
                         aktivRef.current === false ? POLL_MELDER_MS : POLL_MS);
    };
    void laden().finally(() => { if (!geschlossen.current) schleife(); });
    const vis = () => { if (document.visibilityState === 'visible') void laden(); };
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('focus', vis);
    window.addEventListener('online', vis);
    return () => { geschlossen.current = true; if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', vis); window.removeEventListener('focus', vis); window.removeEventListener('online', vis); };
  }, [laden]);

  // Tab-Titel: „(2) Sickinger Tickets“ — auch bei verstecktem Tab sichtbar (nur im Ticket-Modul,
  // damit sich die Einkauf-Zähler im Titel anderer Module nicht in die Quere kommen)
  const n = daten.tickets.length + (daten.melder?.length ?? 0);
  useEffect(() => {
    if (typeof document === 'undefined' || !window.location.hostname.startsWith('tickets.')) return;
    const basis = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = n > 0 ? `(${n}) ${basis}` : basis;
  }, [n]);

  const gesehen = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setDaten(d => ({ ...d, tickets: d.tickets.filter(t => !ids.includes(t.id)) }));
    api.ticketNeu.gesehen(ids).catch(() => { void laden(); });
  }, [laden]);

  const melderGesehen = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setDaten(d => ({ ...d, melder: (d.melder ?? []).filter(h => !ids.includes(h.id)) }));
    api.ticketNeu.gesehenMelder(ids).catch(() => { void laden(); });
  }, [laden]);

  const wert = useMemo<Ctx>(() => ({
    daten, gesehen, istNeu: (id: number) => daten.tickets.some(t => t.id === id),
    melderGesehen, hatHinweis: (id: number) => (daten.melder ?? []).some(h => h.id === id),
  }), [daten, gesehen, melderGesehen]);
  return (
    <TicketNeuContext.Provider value={wert}>
      {children}
      {daten.aktiv && <TicketPopup />}
      <MelderToasts />
    </TicketNeuContext.Provider>
  );
}

/** Kleines rotes „NEU“-Etikett (bleibt, bis das Ticket angeklickt wird) */
export function TicketNeuEtikett() {
  return (
    <span data-testid="ticket-neu-etikett" className="inline-block bg-red-500 text-white font-bold rounded text-[10px] px-1.5 py-0.5 tracking-wide animate-pulse">
      NEU
    </span>
  );
}

/** Rotes Zähler-Badge (Menü „Tickets“) */
export function TicketZaehlerBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span data-testid="ticket-badge" aria-label={`${n} neue Tickets`}
          className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
      {n > 99 ? '99+' : n}
    </span>
  );
}

const CSS = `
@keyframes tkAlarm { 0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,.65), 0 10px 40px rgba(0,0,0,.3); } 50% { box-shadow: 0 0 0 18px rgba(239,68,68,0), 0 10px 40px rgba(0,0,0,.3); } }
@keyframes tkWackeln { 0%, 100% { transform: rotate(0); } 15% { transform: rotate(-14deg); } 30% { transform: rotate(12deg); } 45% { transform: rotate(-9deg); } 60% { transform: rotate(6deg); } 75% { transform: rotate(-3deg); } }
@media (prefers-reduced-motion: reduce) { .tk-anim { animation: none !important; } }
`;
const TYP_ICON: Record<string, string> = { fehler: '🐞', wunsch: '💡', frage: '❓' };

/** Wohin „Ansehen“ führt: auf dem Tickets-Host direkt per Router, sonst zum Ticket-Modul */
export function ticketZiel(pfad = '/tickets'): { intern: boolean; url: string } {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.startsWith('tickets.') || host === 'localhost' || host === '127.0.0.1') return { intern: true, url: pfad };
  return { intern: false, url: `https://tickets.sickinger-portal.de${pfad}` };
}

/** Klartext für den Melder: was ist an meinem Ticket passiert? */
export function melderText(h: TicketMelderHinweis): string {
  if (h.art === 'kommentar') return `${h.von ?? 'Jemand'} hat geschrieben${h.text ? `: „${h.text.length > 90 ? h.text.slice(0, 90) + '…' : h.text}“` : ''}`;
  if (h.art === 'zuweisung') return `${h.von ?? 'Jemand'} kümmert sich jetzt darum`;
  if (h.status === 'rueckfrage') return 'Rückfrage — bitte antworte kurz';
  if (h.status === 'abgelehnt') return `Abgelehnt${h.text ? `: ${h.text.length > 90 ? h.text.slice(0, 90) + '…' : h.text}` : ''}`;
  return `Status: ${statusInfo(h.status).label}${h.von ? ` (${h.von})` : ''}`;
}

/** Kleine Meldungen unten rechts: „Dein Ticket #T-5 …“ — nur für eigene Tickets, nie als Vollbild-Popup */
function MelderToasts() {
  const { daten } = useTicketNeu();
  const navigate = useNavigate();
  const [zeilen, setZeilen] = useState<TicketMelderHinweis[]>([]);
  const hinweise = daten.melder ?? [];

  useEffect(() => {
    if (hinweise.length === 0) return;
    const bekannt = melderGezeigtLesen();
    const neu = hinweise.filter(h => !bekannt.has(melderKey(h)));
    if (neu.length === 0) return;
    for (const h of neu) bekannt.add(melderKey(h));
    melderGezeigtSchreiben(bekannt);
    setZeilen(alt => [...neu.filter(h => !alt.some(a => a.id === h.id)), ...alt.filter(a => !neu.some(h => h.id === a.id))].slice(0, 3));
  }, [hinweise]);

  // Hinweis inzwischen gesehen (z. B. in „Meine Tickets“ geöffnet) → Meldung verschwindet
  useEffect(() => { setZeilen(z => z.filter(x => hinweise.some(h => h.id === x.id))); }, [hinweise]);

  if (zeilen.length === 0) return null;
  const ansehen = (h: TicketMelderHinweis) => {
    setZeilen(z => z.filter(x => x.id !== h.id));
    const ziel = ticketZiel(`/meine-tickets?ticket=${h.id}`);
    if (ziel.intern) navigate(ziel.url); else window.location.href = ziel.url;
  };
  return (
    <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2 w-[min(22rem,calc(100vw-2rem))]" data-testid="melder-toasts">
      {zeilen.map(h => (
        <div key={h.id} data-testid="melder-toast" role="status"
             className="bg-white border border-slate-200 border-l-4 border-l-sky-500 rounded-xl shadow-lg p-3 text-sm">
          <div className="font-semibold text-slate-900">
            <span className="font-mono text-[11px] bg-slate-800 text-white rounded px-1 mr-1">#T-{h.id}</span>
            Dein Ticket hat sich geändert
          </div>
          <div className="text-xs text-slate-500 truncate">{h.titel}</div>
          <div className="mt-1 text-slate-700">{melderText(h)}</div>
          <div className="mt-2 flex gap-2">
            <button data-testid="melder-toast-ansehen" onClick={() => ansehen(h)}
                    className="bg-sky-600 text-white rounded-lg px-3 py-1 text-xs font-semibold hover:opacity-90">Ansehen</button>
            <button data-testid="melder-toast-zu" onClick={() => setZeilen(z => z.filter(x => x.id !== h.id))}
                    className="border border-slate-300 text-slate-600 rounded-lg px-3 py-1 text-xs hover:bg-slate-50">Schließen</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TicketPopup() {
  const { daten } = useTicketNeu();
  const navigate = useNavigate();
  const [zeilen, setZeilen] = useState<typeof daten.tickets>([]);
  const [ton, setTon] = useState(tonAn());
  const boxRef = useRef<HTMLDivElement>(null);
  const [sichtbar, setSichtbar] = useState(typeof document === 'undefined' ? true : document.visibilityState === 'visible');

  useEffect(() => {
    const f = () => setSichtbar(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', f);
    return () => document.removeEventListener('visibilitychange', f);
  }, []);

  // Neues, das noch nie angekündigt wurde → Popup (nur bei sichtbarem Fenster)
  useEffect(() => {
    if (!sichtbar) return;
    const bekannt = gezeigtLesen();
    const neu = daten.tickets.filter(t => !bekannt.has(t.id));
    if (neu.length === 0) return;
    for (const t of neu) bekannt.add(t.id);
    gezeigtSchreiben(bekannt);
    setZeilen(alt => [...neu.filter(t => !alt.some(a => a.id === t.id)), ...alt]);
  }, [daten, sichtbar]);

  // Vorschau: …/tickets?ticket-test=1
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('ticket-test') === '1')
        setZeilen([{ id: 0, titel: 'Drucker im Büro druckt nicht (Vorschau)', typ: 'fehler', prio: 'dringend', bereich: 'Büro', created_at: '', von: 'Jasmin' }]);
    } catch { /* egal */ }
  }, []);

  // Auftritt: Box „springt“ herein + Ton
  const offen = zeilen.length > 0;
  useEffect(() => {
    if (!offen) return;
    try {
      boxRef.current?.animate?.([
        { transform: 'scale(.5)', opacity: 0 },
        { transform: 'scale(1.06)', opacity: 1, offset: 0.6 },
        { transform: 'scale(1)', opacity: 1 },
      ], { duration: 420, easing: 'cubic-bezier(.2,.9,.3,1.2)' });
    } catch { /* ältere Browser */ }
    if (tonAn()) fanfare();
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setZeilen([]); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [offen]);

  if (!offen) return null;
  const name = daten.vorname || 'Chef';
  const zeigen = zeilen.slice(0, 5);
  const dringend = zeilen.some(z => z.prio === 'dringend');
  const ansehen = () => {
    setZeilen([]);
    const z = ticketZiel();
    if (z.intern) navigate(z.url); else window.location.href = z.url;
  };
  return (
    <div data-testid="ticket-popup" role="dialog" aria-label="Neues Ticket"
         className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-red-900/50" onClick={() => setZeilen([])}>
      <style>{CSS}</style>
      <div ref={boxRef} className="tk-anim bg-white rounded-2xl w-full max-w-md overflow-hidden border-4 border-red-500"
           style={{ animation: 'tkAlarm 1.6s ease-in-out infinite' }} onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-red-600 to-rose-500 text-white px-6 py-5 text-center">
          <div className="tk-anim text-5xl inline-block" style={{ animation: 'tkWackeln 1.8s ease-in-out infinite', transformOrigin: '50% 20%' }} aria-hidden>🎫</div>
          <div className="mt-1 inline-block bg-white text-red-600 font-extrabold text-xs rounded px-2 py-0.5 tracking-widest">NEU</div>
          <h2 className="mt-1 text-lg font-bold leading-snug">
            {zeilen.length === 1 ? `${name}, ein neues Ticket für dich!` : `${name}, ${zeilen.length} neue Tickets für dich!`}
            {dringend && ' 🚨'}
          </h2>
        </div>
        <ul className="px-6 py-4 space-y-2 text-sm text-slate-700">
          {zeigen.map(z => (
            <li key={z.id} data-testid="ticket-popup-zeile" className="flex items-start gap-2">
              <span aria-hidden>{TYP_ICON[z.typ] ?? '🎫'}</span>
              <span className="min-w-0">
                <span className="font-semibold text-slate-900">{z.id > 0 && <span className="font-mono text-[11px] bg-slate-800 text-white rounded px-1 mr-1">#T-{z.id}</span>}{z.titel}</span>
                <span className="block text-xs text-slate-500">
                  {z.von ? `von ${z.von}` : ''}{z.bereich ? ` · ${z.bereich}` : ''}
                  {z.prio !== 'normal' && <span className={`ml-1 font-semibold ${z.prio === 'dringend' ? 'text-red-600' : 'text-amber-600'}`}>{z.prio === 'dringend' ? 'DRINGEND' : 'wichtig'}</span>}
                </span>
              </span>
            </li>
          ))}
          {zeilen.length > zeigen.length && <li className="text-slate-400">… und {zeilen.length - zeigen.length} weitere</li>}
        </ul>
        <div className="px-6 pb-5 flex gap-2">
          <button data-testid="ticket-popup-ansehen" onClick={ansehen}
                  className="flex-1 bg-red-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            Ansehen 👀
          </button>
          <button data-testid="ticket-popup-spaeter" onClick={() => setZeilen([])}
                  className="border border-slate-300 text-slate-600 rounded-lg px-4 py-2.5 text-sm hover:bg-slate-50">
            Später
          </button>
        </div>
        <div className="px-6 pb-3 -mt-2 text-right">
          <button data-testid="ticket-popup-ton" className="text-[11px] text-slate-400 hover:text-slate-600"
                  onClick={() => { const nn = !ton; setTon(nn); try { localStorage.setItem(TON_KEY, nn ? '0' : '1'); } catch { /* egal */ } }}>
            {ton ? '🔔 Ton an' : '🔕 Ton aus'}
          </button>
        </div>
      </div>
    </div>
  );
}
